import React from 'react';
import { motion } from 'framer-motion';
import { 
  Landmark, Rocket, Globe, ShieldCheck, 
  ArrowRight, CheckCircle2, FileText, 
  Shield, Briefcase, Building2 
} from 'lucide-react';

const practiceData = [
  {
    id: "01",
    title: "Banking, NBFCs Insurance, Re-Insurance and Insurance Intermediaries, Global Capability Centers",
    icon: <Landmark size={24} />,
    services: [
      { name: "Insurance and Banking Regulatory (e.g. Corporate Agency Compliances, Regulatory Inspections) Advisory", icon: <Landmark size={12} /> },
      { name: "Insurance Product, Reinsurance and Marketing Contracts", icon: <FileText size={12} /> },
      { name: "Managing Compliance including Secretarial and POSH compliances", icon: <Shield size={12} /> },
      { name: "Insurance laws advisory, RBI Regulations and engagement with IRDAI / RBI/ NPCI", icon: <CheckCircle2 size={12} /> },
      { name: "FEMA and FDI Advisory and Compliances", icon: <Globe size={12} /> },
      { name: "Employment Contracts and Workplace Policies and Frameworks in alignment with New Labour Codes", icon: <Briefcase size={12} /> },
      { name: "Compliance Management Operating System", icon: <Shield size={12} /> }
    ]
  },
  {
    id: "02",
    title: "Fintech, E-Commerce, Start-ups",
    icon: <Rocket size={24} />,
    services: [
      { name: "Implementation of Digital Personal Data Protection framework/GDPR Compliances", icon: <Shield size={12} /> },
      { name: "Mergers & Acquisitions; Private Equity, VC Funding, Founders Agreements", icon: <FileText size={12} /> },
      { name: "Domestic and Cross-Border Lending (including De-Fi transactions)", icon: <Globe size={12} /> },
      { name: "Contract Management Services - Vendor & Employment Contracts", icon: <Briefcase size={12} /> },
      { name: "Conducting Legal and Compliance Due Diligence/Title Searches", icon: <CheckCircle2 size={12} /> },
      { name: "IPO readiness and advisory", icon: <Rocket size={12} /> }
    ]
  },
  {
    id: "03",
    title: "Individuals, Small Business Houses, SMSE Owners",
    icon: <ShieldCheck size={24} />,
    services: [
      { name: "Estate Planning - Private Family Trust Deeds; Wills; Living Wills", icon: <FileText size={12} /> },
      { name: "Fractional /Off-Counsel Services", icon: <Briefcase size={12} /> },
      { name: "Simplification and Standardisation of Contracts/MOU/LOIs/Appointment letters", icon: <FileText size={12} /> },
      { name: "Handling POSH (sexual harassment) complaints", icon: <Shield size={12} /> },
      { name: "Consumer litigation and management", icon: <Briefcase size={12} /> },
      { name: "Police/Enforcement Directorate/FIU/RBI/FEMA Liaisoning", icon: <Building2 size={12} /> }
    ]
  }
];

const PracticeAreas = () => {
  const brandNavy = "#0c3754";
  const lightBg = "#e3f4ff";

  return (
    <section style={{ backgroundColor: lightBg }} className="relative px-8 md:px-16 py-32">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
        
        <div className="lg:col-span-4 lg:sticky lg:top-40 h-fit z-[50]">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <h2 style={{ color: brandNavy }} className="text-6xl font-serif leading-tight mb-8">
              Practice <br /> <span className="opacity-30 font-light">Areas</span>
            </h2>
            <div className="h-[1px] w-24 bg-[#0c3754]/20 mb-8" />
            <p style={{ color: brandNavy }} className="text-sm leading-relaxed max-w-xs opacity-70">
              Providing tailored legal and compliance solutions across diverse sectors, 
              ensuring your business stays compliant and protected.
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-8 flex flex-col">
          {practiceData.map((item, index) => (
            <div 
              key={item.id} 
              className="sticky transition-all duration-500"
              style={{ 
                top: `${120 + (index * 30)}px`, 
                zIndex: index + 1,              
                marginBottom: index === practiceData.length - 1 ? '0' : '15vh' 
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-5%" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full min-h-[580px] bg-white border border-[#0c3754]/5 rounded-xl p-10 md:p-12 shadow-[0_40px_80px_-20px_rgba(12,55,84,0.15)] group overflow-hidden"
              >
                <div 
                  style={{ background: `linear-gradient(to top, white, transparent)` }}
                  className="absolute inset-x-0 bottom-0 h-24 z-20 pointer-events-none opacity-90 group-last:hidden" 
                />

                <div className="relative z-30 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-12">
                      <div className="w-12 h-12 rounded-lg bg-[#0c3754]/5 border border-[#0c3754]/10 flex items-center justify-center text-[#0c3754]/40 group-hover:bg-[#0c3754] group-hover:text-white transition-all duration-500">
                        {item.icon}
                      </div>
                      <span className="text-4xl font-serif text-[#0c3754]/5 font-bold">
                        {item.id}
                      </span>
                    </div>

                    <h3 style={{ color: brandNavy }} className="text-3xl md:text-4xl font-serif mb-10 tracking-tight leading-snug">
                      {item.title}
                    </h3>

                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
                      {item.services.map((service, i) => (
                        <div key={i} className="flex items-start gap-3 group/item">
                          <div className="w-6 h-6 rounded-full bg-[#0c3754]/5 flex items-center justify-center text-[#0c3754]/40 group-hover/item:bg-[#0c3754]/10 group-hover/item:text-[#0c3754] transition-all flex-shrink-0 mt-0.5">
                            {service.icon}
                          </div>
                          <span className="text-[#0c3754]/70 text-[12px] font-medium group-hover/item:text-[#0c3754] transition-colors leading-relaxed">
                            {service.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-12 flex items-center justify-between pt-8 border-t border-[#0c3754]/5 bg-white relative z-40">
                     <motion.button 
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.98 }}
                       className="relative px-7 py-2.5 bg-[#0c3754] rounded-lg flex items-center gap-3 overflow-hidden group/btn shadow-lg shadow-[#0c3754]/30"
                     >
                        <span className="text-white text-[12px] font-semibold z-10">
                          Learn more
                        </span>
                        <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center z-10 group-hover/btn:bg-white/20 transition-all">
                          <ArrowRight size={14} className="text-white group-hover/btn:translate-x-1 transition-transform" />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000" />
                     </motion.button>

                     <div className="text-[10px] font-medium opacity-20 text-[#0c3754] hidden sm:block">
                        LCaaS &bull; Global Standards
                     </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
          <div className="h-[30vh]" />
        </div>
      </div>
    </section>
  );
};

export default PracticeAreas;